import { enqueueSnackbar } from "notistack";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { createContext, useContextSelector } from "use-context-selector";
import { getEventById } from "../../api/events";
import { useData } from "../DataContext/DataProvider";
import { useUIContext } from "../UIContext/UIContext";
import axiosInstance from "../../api/axiosInstance";
import { backendUrl } from "../../api/constants";
import { convertEventDate, isDefaultDateFilter } from "../../util/eventUtils";
import { debounce } from "lodash";
import { DateTime } from "luxon";
import { useLocation } from "react-router-dom";
import { defaultDateFilter } from "../../util/dataUtils";
import { useUser } from "../UserContext/UserProvider";
import { handleSpecialFilter } from "../../util/eventUtils";
import { useAnalytics } from "../AnalyticsContext/AnalyticsProvider";

const EventDetailsContext = createContext(null);

// Export context for direct use with useContextSelector
export { EventDetailsContext };

export const EventDetailsProvider = ({ children }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSubEvent, setSelectedSubEvent] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [queryFilter, setQueryFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState([]);
  const [detailsSearchLoading, setDetailsSearchLoading] = useState(false);
  const { userLocation } = useData();
  const { activeCategories, likedFilter, posterFilter } = useUIContext();
  const prevDateFilter = useRef();
  const prevQueryFilter = useRef();
  const  {user} = useUser();
  const location = useLocation();
  const { trackSessionEvent } = useAnalytics();
  
  // Refs to track previous selections for analytics
  const prevSelectedEventRef = useRef(null);
  const prevSelectedSubEventRef = useRef(null);

  const fetchEventDetails = async (eventId, opts = {}) => {
    try {
      const data = await getEventById(eventId, {
        referenceDate: opts.referenceDate,
        userLocation: userLocation,
        includeEvents: opts.includeEvents,
        excludePastEvents: true,
      });
      opts.onSuccess && opts.onSuccess(data);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("failed to fetch event details", { variant: "error" });
    }
  };

  const fetchFilteredEvents = async (options) => {
    let opts = options;
    opts.setLoading && opts.setLoading(true);
    const specialDateFilter = ["now", "soon", "later", "late night"].includes(
      opts.dateFilter
    );
    const hasSearchQuery = opts.search && opts.search.trim() !== "";
    
    // Check if dateFilter is null or default BEFORE setting from/to
    const isDefaultDate = opts.dateFilter && isDefaultDateFilter(opts.dateFilter);
    const shouldUse6Months = hasSearchQuery && (!opts.dateFilter || isDefaultDate);
    
    console.log(`📅 [EventDetails fetchFilteredEvents] hasSearch: ${hasSearchQuery}, dateFilter: ${opts.dateFilter ? 'exists' : 'null'}, isDefault: ${isDefaultDate}, shouldUse6Months: ${shouldUse6Months}`);
    
    // console.log("specialDateFilter ", specialDateFilter);
    if (specialDateFilter) {
      const ddate = defaultDateFilter(opts.timezone);
      opts.from = ddate.start;
      opts.to = ddate.end;
    } else if (!opts.from && !opts.to) {
      // If there's a search query AND (no dateFilter OR dateFilter is default 2-day), use 6 months
      // Otherwise use default (2 days)
      const endPlus = shouldUse6Months ? { months: 6 } : { days: 2 };
      const ddate = defaultDateFilter(opts.timezone, { endPlus });
      opts.from = ddate.start;
      opts.to = ddate.end;
      console.log(`📅 [EventDetails] Setting from/to dates, using ${shouldUse6Months ? '6 months' : '2 days'} default`);
    } else if (shouldUse6Months && opts.from && opts.to) {
      // Even if from/to are set, if they match default dates and there's a search, override to 6 months
      const endPlus = { months: 6 };
      const ddate = defaultDateFilter(opts.timezone, { endPlus });
      opts.from = ddate.start;
      opts.to = ddate.end;
      console.log(`📅 [EventDetails] Overriding from/to dates with 6 months for search query`);
    }

    opts.onSuccess = (results) => {
      // console.log("fetchFilteredEvents results ", results);
      if (results) {
        // Add venue details to filtered events
        const processedResults = results.map(event => {
          if (event.venue === selectedEvent?._id) {
            return {
              ...event,
              venueDetails: {
                _id: selectedEvent._id,
                name: selectedEvent.name
              }
            };
          }
          return event;
        });

        if (specialDateFilter) {
          const presults = handleSpecialFilter(processedResults, opts.dateFilter);
          console.log("presults ", presults);
          setFilteredEvents(presults);
        } else {
          setFilteredEvents(processedResults);
        }
      }
    };

    performSearch(opts);
  };

  const performSearch = useCallback(
    debounce(async (options) => {
      const query = options.search;
      const venue = options.venue;
      const from = options.from;
      const to = options.to;
      // console.log("search query", query);
      let fromDate, toDate;
      const timezone = options.timezone || "UTC";
      const tags = options.tagFilter;
      // console.log("timezone ", timezone);

      // Date filter has to be considered in the selected event time zone and then convert it to utc
      if (from) {
        fromDate = DateTime.fromJSDate(from)
          .setZone(timezone, {
            keepLocalTime: true,
          })
          .toUTC()
          .toISO();
      }
      if (to) {
        toDate = DateTime.fromJSDate(to)
          .setZone(timezone, {
            keepLocalTime: true,
          })
          .toUTC()
          .toISO();
      }

      options.setLoading && options.setLoading(true);
      let url = `${backendUrl}/events/search?`;
      url += `limit=all&`;
      url += `categories=${activeCategories.join(",")}&`;
      //add excludePastEvents query
      url += `excludePastEvents=true&`;
      if (tags) {
        url += `tags=${tags?.join(",")}&`;
      }
      if (query) {
        url += `search=${query}&`;
      }
      if (likedFilter) {
        url += `liked=${likedFilter}&`;
      }
      if (posterFilter) {
        url += `posterliked=${posterFilter}&`;
      }
      if (venue) {
        url += `venue=${venue}&`;
      }
      if (fromDate) {
        url += `from=${fromDate}&`;
      }
      if (toDate) {
        url += `to=${toDate}`;
      }
      const { data } = await axiosInstance.get(url);
      options.setLoading && options.setLoading(false);
      if (data.error) {
        enqueueSnackbar(data.error, { variant: "error" });
        return;
      }
      // console.log("preformat search data :", data.data?.[0]);
      const searchData = data.data?.[0] || {};

      //TODO centralize this
      const formateddata =
        searchData.events?.map((item) => {
          //~ Convert Date to Local
          if (item.type === "event") {
            convertEventDate(item);
          } else if (item.type === "venue" && item.events) {
            item.events.forEach((event) => {
              convertEventDate(event);
            });
            if (item._matchingEvent) {
              convertEventDate(item._matchingEvent);
            }
          }

          return item;
        }) || [];

      // console.log("search data ", formateddata);

      options.onSuccess && options.onSuccess(formateddata);
    }, 500),
    [
      userLocation?.lat,
      userLocation?.lng,
      activeCategories,
      likedFilter,
      posterFilter,
      activeCategories,
      tagFilter,
    ]
  );

  const fetchQueryEvent = async (eventId) => {
    try {
      const data = await getEventById(eventId, {
        userLocation: userLocation,
        includeEvents: true,
      });
      console.log(">>>>>>>>>>> fetched query data :", data);
      setSelectedEvent((prev) => {
        const updated = { ...prev, ...data };
        // If the new response doesn't include events, remove it from the state.
        if (data && !("events" in data)) {
          delete updated.events;
        }
        return updated;
      });
      if (data._matchingEvent) {
        setSelectedSubEvent(data._matchingEvent);
      } else {
        // Clear any previously selected subevent if the new event doesn't have a matching event
        setSelectedSubEvent(null);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("failed to fetch query event details", {
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (!selectedEvent?._id) {
      return;
    }

    if (selectedEvent?.timezone?.[0]) {
      fetchFilteredEvents({
        search: queryFilter,
        categories: activeCategories,
        venue: selectedEvent?._id,
        from: dateFilter?.start,
        to: dateFilter?.end,
        dateFilter,
        tagFilter,
        setLoading: setDetailsSearchLoading,
        timezone: selectedEvent?.timezone[0],
      });
    }
    // }

    prevDateFilter.current = dateFilter;
    prevQueryFilter.current = queryFilter;
  }, [
    dateFilter,
    tagFilter,
    queryFilter,
    selectedEvent?._id,
    activeCategories,
  ]);

  //#region fetch event details
  const sanitizeQuery = (query) => {
    const decoded = decodeURIComponent(query);
    //if contains #/?(...) extract the query
    if (decoded.includes("#/?")) {
      return decoded.split("#/?")[1];
    }

    return decoded;
  };

  useEffect(() => {
    if(!user){
      return;
    }

    const handleLocationSearch = () => {
      const queryParams = new URLSearchParams(location.search);
      const locationId = queryParams.get("location");
      if (locationId) {
        fetchQueryEvent(locationId);
      } else {
        //extract location from decoded url params
        const sanitized = sanitizeQuery(window.location.search);
        const urlParam = new URLSearchParams(sanitized); // Parse the query string
        const loc = urlParam.get("location");
        if (loc) {
          fetchQueryEvent(loc);
        }
      }
    };

    window.addEventListener("popstate", handleLocationSearch);
    handleLocationSearch();

    return () => window.removeEventListener("popstate", handleLocationSearch);
    // }
  }, [location,user]);

  // FIX: Clear stale dateFilter immediately when venue changes to prevent wrong date in first API call
  useEffect(() => {
    if (selectedEvent?._id) {
      setDateFilter(null);
    }
  }, [selectedEvent?._id]);

  // PERFORMANCE: Defer API calls and heavy date calculations until after animation completes (220ms)
  useEffect(() => {
    if (selectedEvent?._id && userLocation) {
      const eventId = selectedEvent._id; // Capture ID at start to prevent stale closure
      
      // Defer to after animation window to prevent blocking
      const timer = setTimeout(() => {
        // RACE CONDITION FIX: Only fetch if event is STILL selected when timer fires
        if (!selectedEvent?._id || selectedEvent._id !== eventId) {
          return; // Event was closed, abort
        }
        
        // NOTE: dateFilter is managed by Map.jsx via setEventDetailsDateFilter
        // We don't set a default here to avoid racing with Map.jsx's sync (which also happens at ~220ms)
        // If dateFilter is null, fetchFilteredEvents will use 6-month default
        
        const refDate = defaultDateFilter(selectedEvent.timezone?.[0])?.start;
        fetchEventDetails(eventId, {
          includeEvents: true,
          referenceDate: DateTime.fromJSDate(refDate)
            .setZone(selectedEvent.timezone?.[0], {
              keepLocalTime: true,
            })
            .toUTC()
            .toISO(),
          onSuccess: (data) => {
            // Double-check event is still selected when API responds
            if (selectedEvent?._id === eventId && data?._id === eventId) {
              setSelectedEvent({
                ...selectedEvent,
                ...data,
              });
            }
          },
        });
      }, 220);
      
      return () => clearTimeout(timer);
    }
  }, [selectedEvent?._id, userLocation]);

  // PERFORMANCE: Defer sub-event API calls and heavy date calculations until after animation
  useEffect(() => {
    if (selectedSubEvent?._id && userLocation && selectedEvent?.timezone) {
      const subEventId = selectedSubEvent._id; // Capture ID at start to prevent stale closure
      
      // Defer to after animation window to prevent blocking
      const timer = setTimeout(() => {
        // RACE CONDITION FIX: Only fetch if sub-event is STILL selected when timer fires
        if (!selectedSubEvent?._id || selectedSubEvent._id !== subEventId) {
          return; // Sub-event was closed, abort
        }
        
        const refDate = defaultDateFilter(selectedEvent.timezone?.[0])?.start;
        fetchEventDetails(subEventId, {
          referenceDate: DateTime.fromJSDate(refDate)
            .setZone((selectedSubEvent?.timezone || selectedEvent.timezone)?.[0], {
              keepLocalTime: true,
            })
            .toUTC()
            .toISO(),
          onSuccess: (data) => {
            // Double-check sub-event is still selected when API responds
            if (selectedSubEvent?._id === subEventId && 
                data?._matchingEvent?._id === subEventId) {
              setSelectedSubEvent({
                ...selectedSubEvent,
                ...data?._matchingEvent,
              });
            }
          },
        });
      }, 220);
      
      return () => clearTimeout(timer);
    }
  }, [selectedEvent, selectedSubEvent?._id, userLocation]);

  // PERFORMANCE: Defer localStorage operations using requestIdleCallback or setTimeout
  const storeSeenArtist = (event) => {
    if (event.type === "event" && event?.account_id) {
      // Defer to avoid blocking animation
      const deferredStore = () => {
        const storedArtists = JSON.parse(localStorage.getItem("seenArtists") || "[]");
        const artistExists = storedArtists.some(artist =>
            artist._id === event.account_id?._id || artist._id === event.account_id
        );
        if (!artistExists) {
          const artist = event.account_id?._id ? event.account_id : {_id: event.account_id}
          const updatedArtists = [artist, ...storedArtists].slice(0, 10);
          localStorage.setItem("seenArtists", JSON.stringify(updatedArtists));
        }
      };
      
      // Use requestIdleCallback if available, otherwise setTimeout
      if ('requestIdleCallback' in window) {
        requestIdleCallback(deferredStore);
      } else {
        setTimeout(deferredStore, 300);
      }
    }
  };

  useEffect(() => {
    if(selectedEvent) {
      storeSeenArtist(selectedEvent);
    }
    if(selectedSubEvent) {
      storeSeenArtist(selectedSubEvent);
    }
  }, [selectedEvent, selectedSubEvent]);


  const selectEvent = (event, subEv) => {
    // OPTIMIZED: Direct state updates - React 18 automatically batches these into a single render
    // Removed requestAnimationFrame to avoid 16ms delay before animation starts
    if (event?.venueDetails?._id || event?.venue?._id) {
      const venueData = event.venueDetails || event.venue;
      
      // PERFORMANCE: Only create new object if type field needs to be added
      // This prevents unnecessary re-renders from object reference changes
      if (venueData.type === "venue") {
        // Already has correct type, reuse existing object
        setSelectedEvent(venueData);
      } else {
        // Need to add type field, create new object
        setSelectedEvent({
          ...venueData,
          type: "venue"
        });
      }
      setSelectedSubEvent(event);
    } else {
      // React 18 automatically batches these into a single render
      setSelectedEvent(event);
      setSelectedSubEvent(subEv);
    }
  };

  const selectSubEvent = (subEv) => {
    // OPTIMIZED: Direct state update for immediate response
    setSelectedSubEvent(subEv);
  };

  // Clear selectedEvent and selectedSubEvent on dashboard route (mobile only)
  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setSelectedEvent(null);
      setSelectedSubEvent(null);
    }
  }, [location.pathname]);

  // ANALYTICS: Track selectedEvent changes (venues and events)
  useEffect(() => {
    // If event is null, clear the ref and skip tracking
    if (!selectedEvent) {
      console.log('🔍 [ANALYTICS] Provider: No selectedEvent, clearing ref');
      prevSelectedEventRef.current = null;
      return;
    }

    // Skip if it's the same as previous
    if (prevSelectedEventRef.current?._id === selectedEvent._id) {
      console.log('⏭️ [ANALYTICS] Provider: Same event as previous, skipping:', {
        prevId: prevSelectedEventRef.current?._id,
        currentId: selectedEvent._id
      });
      return;
    }

    // Ensure type is set (default to 'event' if missing)
    // FIX: Explicitly validate and set type to prevent "undefined opened" tracking
    let eventType = selectedEvent.type;
    if (!eventType || eventType === 'undefined' || typeof eventType !== 'string') {
      eventType = 'event'; // Default to 'event' if type is missing or invalid
      console.warn('⚠️ [ANALYTICS] Invalid event type detected, defaulting to "event":', {
        originalType: selectedEvent.type,
        eventId: selectedEvent._id,
        eventName: selectedEvent.name
      });
    }

    console.log('✨ [ANALYTICS] Provider: selectedEvent changed, tracking:', {
      type: eventType,
      id: selectedEvent._id,
      name: selectedEvent.name,
      prevId: prevSelectedEventRef.current?._id,
      hasUser: !!user,
      rawType: selectedEvent.type // Show if it was undefined
    });

    // Track the event/venue open
    trackSessionEvent({
      action: `${eventType} opened`,
      category: eventType,
      label: `${eventType}_opened`,
      [eventType]: selectedEvent._id,
      value: {
        name: selectedEvent.name,
        address: selectedEvent.address,
        category: selectedEvent.category,
      },
      timestamp: Date.now(),
    });

    console.log('✅ [ANALYTICS] Provider: Event tracking COMPLETED for:', selectedEvent.name);

    // Update ref to current event
    prevSelectedEventRef.current = selectedEvent;
  }, [selectedEvent, trackSessionEvent, user]);

  // ANALYTICS: Track selectedSubEvent changes (individual events within venues)
  useEffect(() => {
    // If sub-event is null, clear the ref and skip tracking
    if (!selectedSubEvent) {
      console.log('🔍 [ANALYTICS] Provider: No selectedSubEvent, clearing ref');
      prevSelectedSubEventRef.current = null;
      return;
    }

    // Skip if it's the same as previous
    if (prevSelectedSubEventRef.current?._id === selectedSubEvent._id) {
      console.log('⏭️ [ANALYTICS] Provider: Same sub-event as previous, skipping:', {
        prevId: prevSelectedSubEventRef.current?._id,
        currentId: selectedSubEvent._id
      });
      return;
    }

    // Ensure type is set (default to 'event' if missing)
    // FIX: Explicitly validate and set type to prevent "undefined opened" tracking
    let eventType = selectedSubEvent.type;
    if (!eventType || eventType === 'undefined' || typeof eventType !== 'string') {
      eventType = 'event'; // Default to 'event' if type is missing or invalid
      console.warn('⚠️ [ANALYTICS] Invalid sub-event type detected, defaulting to "event":', {
        originalType: selectedSubEvent.type,
        eventId: selectedSubEvent._id,
        eventName: selectedSubEvent.name
      });
    }

    console.log('✨ [ANALYTICS] Provider: selectedSubEvent changed, tracking:', {
      type: eventType,
      id: selectedSubEvent._id,
      name: selectedSubEvent.name,
      prevId: prevSelectedSubEventRef.current?._id,
      hasUser: !!user,
      rawType: selectedSubEvent.type // Show if it was undefined
    });

    // Track the sub-event open
    trackSessionEvent({
      action: `${eventType} opened`,
      category: eventType,
      label: `${eventType}_opened`,
      [eventType]: selectedSubEvent._id,
      value: {
        name: selectedSubEvent.name,
        address: selectedSubEvent.address,
        category: selectedSubEvent.category,
      },
      timestamp: Date.now(),
    });

    console.log('✅ [ANALYTICS] Provider: Sub-event tracking COMPLETED for:', selectedSubEvent.name);

    // Update ref to current sub-event
    prevSelectedSubEventRef.current = selectedSubEvent;
  }, [selectedSubEvent, trackSessionEvent, user]);

  const value = useMemo(() => ({
    selectedEvent,
    setSelectedEvent,
    selectedSubEvent,
    setSelectedSubEvent,
    selectEvent,
    selectSubEvent,
    dateFilter,
    setDateFilter,
    tagFilter,
    setTagFilter,
    queryFilter,
    setQueryFilter,
    filteredEvents,
    setFilteredEvents,
    detailsSearchLoading,
    setDetailsSearchLoading,
  }), [
    selectedEvent,
    setSelectedEvent,
    selectedSubEvent,
    setSelectedSubEvent,
    selectEvent,
    selectSubEvent,
    dateFilter,
    setDateFilter,
    tagFilter,
    setTagFilter,
    queryFilter,
    setQueryFilter,
    filteredEvents,
    setFilteredEvents,
    detailsSearchLoading,
    setDetailsSearchLoading,
  ]);
  return (
    <EventDetailsContext.Provider value={value}>
      {children}
    </EventDetailsContext.Provider>
  );
};


// Hook for only selectEvent and selectSubEvent (stable, won't cause re-renders)
export const useEventDetailsSetters = () => {
  const selectEvent = useContextSelector(EventDetailsContext, state => state.selectEvent);
  const selectSubEvent = useContextSelector(EventDetailsContext, state => state.selectSubEvent);
  const setSelectedEvent = useContextSelector(EventDetailsContext, state => state.setSelectedEvent);
  const setSelectedSubEvent = useContextSelector(EventDetailsContext, state => state.setSelectedSubEvent);
  const setDateFilter = useContextSelector(EventDetailsContext, state => state.setDateFilter);
  const setQueryFilter = useContextSelector(EventDetailsContext, state => state.setQueryFilter);
  const setTagFilter = useContextSelector(EventDetailsContext, state => state.setTagFilter);
  return {
    selectEvent,
    selectSubEvent,
    setSelectedEvent,
    setSelectedSubEvent,
    setDateFilter,
    setQueryFilter,
    setTagFilter,
  };
};

// Hook for only selected event IDs (minimal re-renders for MapMarker selection)
export const useSelectedEventIds = () => {
  const selectedEventId = useContextSelector(EventDetailsContext, state => state.selectedEvent?._id);
  const selectedSubEventId = useContextSelector(EventDetailsContext, state => state.selectedSubEvent?._id);
  return {
    selectedEventId,
    selectedSubEventId,
  };
};

// Combined hook for backward compatibility
export const useEventDetails = () => {
  const context = useContextSelector(EventDetailsContext, state => state);
  if (!context) {
    throw new Error(
      "useEventDetails must be used within a EventDetailsProvider"
    );
  }
  return context;
};
